import React, { useState, useEffect } from 'react';
import { apiClient } from '../../utils/apiClient';

const API = process.env.REACT_APP_API_BASE_URL + '/subscription_api.php';

const FEATURE_LABELS = {
    courses_read:    'Accès aux cours',
    flashcards:      'Flashcards & révisions',
    quiz:            'Quiz',
    ai_chat:         'Chat IA (Axio)',
    skills:          'Exercices & compétences',
    ai_course_gen:   'Génération de cours IA',
    schedule_cadre:  'Mon Planning',
    schedule_ai_seq: 'Séquences IA de révision',
    history:         'Historique des conversations',
};

const PLAN_COLORS = {
    free:      { grad: 'from-gray-500 to-gray-600', badge: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300', border: 'border-gray-200 dark:border-gray-700' },
    essentiel: { grad: 'from-cyan-500 to-blue-600', badge: 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300', border: 'border-cyan-400 dark:border-cyan-600' },
    premium:   { grad: 'from-purple-500 to-indigo-600', badge: 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300', border: 'border-purple-400 dark:border-purple-600' },
};

function TokenBudgetBar({ budget }) {
    if (!budget || budget.limit === 0) return null;
    const pct = Math.min(100, Math.round((budget.used / budget.limit) * 100));
    const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-orange-400' : 'bg-cyan-500';
    return (
        <div className="bg-white/70 dark:bg-white/[0.03] backdrop-blur-xl rounded-2xl border border-gray-200 dark:border-white/[0.05] p-4 mb-6">
            <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Utilisation IA cette heure</span>
                <span className="text-sm text-gray-500">{pct}%</span>
            </div>
            <div className="w-full bg-gray-100 dark:bg-white/10 rounded-full h-2">
                <div className={`${color} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
            </div>
            {pct >= 90 && (
                <p className="text-xs text-red-500 mt-1">Limite presque atteinte — se renouvelle à la prochaine heure.</p>
            )}
        </div>
    );
}

function PlanCard({ plan, isCurrentPlan, billing, onSelect }) {
    const [selecting, setSelecting] = useState(false);
    const colors = PLAN_COLORS[plan.slug] || PLAN_COLORS.free;
    const price  = parseFloat(billing === 'yearly' ? plan.price_yearly : plan.price_monthly) || 0;
    const priceLabel = price === 0 ? 'Gratuit' : `${price.toFixed(2).replace('.', ',')} €`;
    const perLabel   = price === 0 ? '' : billing === 'yearly' ? '/ an' : '/ mois';

    const handleSelect = async () => {
        if (selecting) return;
        setSelecting(true);
        await onSelect(plan.slug);
        setSelecting(false);
    };

    return (
        <div className={`relative flex flex-col rounded-3xl border-2 ${isCurrentPlan ? colors.border : 'border-gray-200 dark:border-white/[0.05]'} bg-white/70 dark:bg-white/[0.03] backdrop-blur-xl overflow-hidden transition-all ${isCurrentPlan ? 'shadow-lg scale-[1.02]' : 'hover:shadow-md'}`}>
            {/* Header */}
            <div className={`bg-gradient-to-br ${colors.grad} p-6 text-white`}>
                {isCurrentPlan && (
                    <div className="text-xs font-semibold uppercase tracking-wide opacity-80 mb-1">Plan actuel</div>
                )}
                <h2 className="text-2xl font-bold">{plan.name}</h2>
                <p className="text-sm opacity-80 mt-1">{plan.description}</p>
                <div className="mt-4">
                    <span className="text-4xl font-extrabold">{priceLabel}</span>
                    {perLabel && <span className="text-sm opacity-70 ml-1">{perLabel}</span>}
                </div>
                {billing === 'yearly' && price > 0 && (
                    <div className="text-xs opacity-70 mt-1">
                        soit {(price / 12).toFixed(2).replace('.', ',')} €/mois
                    </div>
                )}
                <div className="mt-2 text-xs opacity-70">
                    {parseInt(plan.token_limit_hour, 10) === 0 ? 'Utilisation IA illimitée' : 'Utilisation IA standard'}
                </div>
            </div>

            {/* Features */}
            <div className="flex-1 p-6 space-y-2.5">
                {Object.entries(FEATURE_LABELS).map(([key, label]) => {
                    const included = plan.features?.[key] ?? false;
                    return (
                        <div key={key} className={`flex items-center gap-3 text-sm ${included ? 'text-gray-700 dark:text-gray-200' : 'text-gray-300 dark:text-gray-600 line-through'}`}>
                            <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${included ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' : 'bg-gray-100 dark:bg-white/5 text-gray-400'}`}>
                                {included ? '✓' : '×'}
                            </span>
                            {label}
                        </div>
                    );
                })}
            </div>

            {/* CTA */}
            <div className="px-6 pb-6">
                {isCurrentPlan ? (
                    <div className={`w-full py-3 rounded-2xl text-center text-sm font-semibold ${colors.badge}`}>
                        Abonnement actuel
                    </div>
                ) : price === 0 ? (
                    <button onClick={handleSelect} disabled={selecting}
                        className="w-full py-3 rounded-2xl text-sm font-medium text-gray-500 bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 disabled:opacity-50 transition-colors">
                        {selecting ? 'Activation…' : 'Passer au plan gratuit'}
                    </button>
                ) : (
                    <button onClick={handleSelect} disabled={selecting}
                        className={`w-full py-3 rounded-2xl text-white text-sm font-semibold bg-gradient-to-r ${colors.grad} hover:opacity-90 disabled:opacity-50 transition-opacity`}>
                        {selecting ? 'Activation…' : 'Choisir ce plan'}
                    </button>
                )}
            </div>
        </div>
    );
}

export default function SubscriptionView({ user, onExit }) {
    const [plans, setPlans]       = useState([]);
    const [mySub, setMySub]       = useState(null);
    const [budget, setBudget]     = useState(null);
    const [billing, setBilling]   = useState('monthly');
    const [loading, setLoading]   = useState(true);
    const [error, setError]       = useState('');
    const [switchMsg, setSwitchMsg] = useState('');

    useEffect(() => {
        Promise.all([
            apiClient(`${API}?action=plans`).then(r => r.json()),
            apiClient(`${API}?action=my_subscription`).then(r => r.json()),
        ]).then(([plansData, subData]) => {
            if (plansData.success) setPlans(plansData.plans);
            if (subData.success) {
                setMySub(subData.plan);
                setBudget(subData.token_budget);
            }
            setLoading(false);
        }).catch(err => {
            console.error('SubscriptionView error:', err);
            setError('Impossible de charger les informations d\'abonnement.');
            setLoading(false);
        });
    }, []);

    const handleSelectPlan = async (slug) => {
        setSwitchMsg('');
        try {
            const res = await apiClient(`${API}?action=switch_plan`, {
                method: 'POST',
                body: JSON.stringify({ plan_slug: slug }),
            }).then(r => r.json());
            if (res.success) {
                setMySub(prev => ({ ...prev, slug: res.plan_slug, name: res.plan_name }));
                setSwitchMsg(`Plan « ${res.plan_name} » activé.`);
                setTimeout(() => setSwitchMsg(''), 4000);
            } else {
                setSwitchMsg(res.message || 'Erreur lors du changement de plan.');
            }
        } catch {
            setSwitchMsg('Erreur de connexion au serveur.');
        }
    };

    const currentSlug = mySub?.slug ?? 'free';

    return (
        <div className="animate-fade-in relative z-10 text-gray-800 dark:text-gray-200 min-h-[80vh]">
            <button onClick={onExit} className="mb-6 text-gray-500 hover:text-gray-700 dark:hover:text-white transition-colors text-sm">
                ← Retour au tableau de bord
            </button>

            <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">Mon abonnement</h1>
                <p className="text-gray-500">Choisis le plan qui correspond à tes objectifs</p>

                {/* Billing toggle */}
                <div className="inline-flex items-center gap-2 mt-4 bg-gray-100 dark:bg-white/5 rounded-2xl p-1">
                    <button onClick={() => setBilling('monthly')}
                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${billing === 'monthly' ? 'bg-white dark:bg-white/10 shadow text-gray-800 dark:text-white' : 'text-gray-500'}`}>
                        Mensuel
                    </button>
                    <button onClick={() => setBilling('yearly')}
                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${billing === 'yearly' ? 'bg-white dark:bg-white/10 shadow text-gray-800 dark:text-white' : 'text-gray-500'}`}>
                        Annuel
                        <span className="text-xs font-bold text-green-500 bg-green-100 dark:bg-green-900/30 px-1.5 py-0.5 rounded-lg">-20%</span>
                    </button>
                </div>
            </div>

            {switchMsg && (
                <div className={`mb-4 px-4 py-3 rounded-2xl text-sm text-center font-medium ${switchMsg.includes('Erreur') ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400' : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'}`}>
                    {switchMsg}
                </div>
            )}

            {loading ? (
                <div className="text-center py-20 text-gray-400">Chargement…</div>
            ) : error ? (
                <div className="text-center py-20 text-red-500">{error}</div>
            ) : (
                <>
                    <TokenBudgetBar budget={budget} />

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {plans.map(plan => (
                            <PlanCard
                                key={plan.id}
                                plan={plan}
                                isCurrentPlan={plan.slug === currentSlug}
                                billing={billing}
                                onSelect={handleSelectPlan}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
